import { useEffect, useMemo, useState } from 'react';
import { usePaginationAndSort } from '@restore/helpers';
import { debounce } from 'lodash';
import { useExternalUserSubjectsSlice } from '@restore/ExternalUserSubjects';
import { useParams } from 'react-router-dom';

export type Filters = {
  patientCode: string;
};

export const useSubjectListFilters = () => {
  const [patientCode, setPatientCode] = useState<string>('');

  const { clinicalTrialId } = useParams<{
    clinicalTrialId: string;
  }>();

  const [search, fetchExternalUserSubjectsList, setSearch, selectedSiteFilter] =
    useExternalUserSubjectsSlice((state, actions) => [
      state.search,
      actions.fetchExternalUserSubjectsList,
      actions.setSearchValue,
      state.selectedSiteFilter,
    ]);

  const { setSortBy, sortBy, currentPage, currentPageSize, setCurrentPage } =
    usePaginationAndSort('externalUserSubjects');

  const onSort = (name: string, dir?: string) => {
    const sort = dir ? `${name},${dir}` : undefined;
    setSortBy({ listName: 'externalUserSubjects', newSortBy: sort });
  };

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        if ((value && value.length > 1) || value === '') {
          setCurrentPage({ listName: 'externalUserSubjects', newPage: 1 });
          setSearch(value);
        }
      }, 500),
    [patientCode]
  );

  useEffect(() => {
    if (clinicalTrialId) {
      fetchExternalUserSubjectsList({
        clinicalTrialId,
        args: {
          patientCode: search,
          sort: sortBy ? [sortBy] : undefined,
          page: currentPage,
          size: currentPageSize,
          siteId: selectedSiteFilter || undefined,
        },
      });
    }
  }, [
    clinicalTrialId,
    search,
    sortBy,
    currentPage,
    currentPageSize,
    selectedSiteFilter,
  ]);

  return {
    patientCode,
    setPatientCode,
    onSort,
    onSearch,
    setSortBy,
    sortBy,
    currentPage,
    currentPageSize,
    setCurrentPage,
  };
};
